import type { ChPostMetrics } from '../lib/metrics';
export type { ChPostMetrics } from '../lib/metrics';

export type ChPlatformMetrics = Partial<Record<ChPostPlatform, ChPostMetrics>>;
export type ChFollowers = Partial<Record<ChPostPlatform, number>>;

export type ChUserRole = 'admin' | 'project_manager' | 'talent' | 'client';
export type ChPostPlatform = 'instagram' | 'tiktok' | 'youtube' | 'podcast';
export type ChPostContentType = 'reels' | 'post_estatico' | 'stories' | 'carrossel' | 'video' | 'episodio' | 'short';
export type ChPostStatus = 'ideia' | 'em_producao' | 'pronto' | 'em_revisao' | 'aprovado' | 'publicado';
export type ChClientDecision = 'pending' | 'approved' | 'changes_requested';
export type ChCommentType = 'internal' | 'client';
export type ChNotificationType =
  | 'approved'
  | 'commented'
  | 'changes_requested'
  | 'assigned'
  | 'client_approved'
  | 'mentioned'
  | 'added_to_project';
export type ChApprovalAction = 'internal_approved' | 'client_approved' | 'changes_requested' | 'published' | 'unapproved';

export interface ChProfile {
  id: string;
  name: string;
  email: string;
  role: ChUserRole;
  avatar_url: string | null;
  created_at: string;
}

export interface ChProject {
  id: string;
  name: string;
  slug: string;
  workcode: string | null;
  tone_profile: string | null;
  archived: boolean;
  created_at: string;
  /** Followers per platform, typed by the team (absent until the v2 SQL). */
  followers?: ChFollowers | null;
  followers_updated_at?: string | null;
}

export interface ChBranding {
  project_id: string;
  logo_url: string | null;
  primary_color: string;
  background_color: string;
  background_image_url: string | null;
  accent_color: string | null;
  updated_at: string;
}

export interface ChPost {
  id: string;
  project_id: string;
  title: string;
  caption: string | null;
  /** First of `platforms`; kept for filters and older code. */
  platform: ChPostPlatform;
  /** Every platform the item goes to (absent until the v2 SQL). */
  platforms?: ChPostPlatform[] | null;
  content_type: ChPostContentType;
  scheduled_date: string | null;
  due_date: string | null;
  /** First of `assignee_ids`. */
  assignee_id: string | null;
  assignee_ids?: string[] | null;
  talent_id: string | null;
  external_link: string | null;
  /** First of `media_urls`. */
  media_url: string | null;
  /** Every file, in order: a carousel has several (max 20). */
  media_urls?: string[] | null;
  /** Cover designed on purpose (e.g. a Reels cover); not a copy of the media. */
  thumbnail_url: string | null;
  status: ChPostStatus;
  client_decision: ChClientDecision | null;
  current_version: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Typed stats; absent until the stats SQL is applied. */
  metrics?: ChPostMetrics | null;
  /** Stats per platform when the item went to more than one. */
  metrics_by_platform?: ChPlatformMetrics | null;
  metrics_updated_at?: string | null;
  /** When it went live, with the time (for the best-time insight). */
  published_at?: string | null;
}

/** Screenshot of a platform's insights for a content item. */
export interface ChPostPrint {
  id: string;
  post_id?: string;
  url: string;
  caption: string | null;
  position?: number;
  created_at?: string;
}

export interface ChComment {
  id: string;
  post_id: string;
  author_id: string | null;
  author_name: string;
  type: ChCommentType;
  text: string;
  created_at: string;
  /** Profiles tagged with @ (absent until the mentions SQL is applied). */
  mentions?: string[] | null;
}

export interface ChNotification {
  id: string;
  user_id: string;
  post_id: string | null;
  project_id?: string | null;
  type: ChNotificationType;
  message: string;
  read: boolean;
  created_at: string;
}

export interface ChUserProject {
  user_id: string;
  project_id: string;
}

export const POST_STATUS_LABELS: Record<ChPostStatus, string> = {
  ideia: 'Ideia',
  em_producao: 'Em Produção',
  pronto: 'Pronto',
  em_revisao: 'Em Revisão',
  aprovado: 'Aprovado',
  publicado: 'Publicado',
};

export const PLATFORM_LABELS: Record<ChPostPlatform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  podcast: 'Podcast',
};

export const CONTENT_TYPE_LABELS: Record<ChPostContentType, string> = {
  reels: 'Reels',
  post_estatico: 'Post Estático',
  stories: 'Stories',
  carrossel: 'Carrossel',
  video: 'Vídeo',
  episodio: 'Episódio',
  short: 'Short',
};

// Three levels: Admin (everything), Account (edits content — stored as
// 'project_manager'), Cliente (sees their projects, comments and approves).
// 'talent' is legacy and behaves as Cliente.
export const ROLE_LABELS: Record<ChUserRole, string> = {
  admin: 'Admin',
  project_manager: 'Account',
  talent: 'Cliente',
  client: 'Cliente',
};

export const ROLE_DESCRIPTIONS: Record<'admin' | 'project_manager' | 'client', string> = {
  admin: 'Tudo o que o Account faz, mais gerir utilizadores e papéis',
  project_manager: 'Cria, edita, arquiva e apaga projetos; todo o conteúdo, stats e links',
  client: 'Vê os seus projetos, comenta e aprova',
};

export function isChStaffRole(role: ChUserRole | null | undefined): boolean {
  return role === 'admin' || role === 'project_manager';
}
